/**
 * ColourMatch Pro · CMS · Project Detail (Phase 2).
 *
 * Route: /pro/cms/projects/:id
 *
 * Layout
 * ------
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ Header · project name · edit · back                              │
 *   ├────────────────────┬────────────────────────────────────────────┤
 *   │ Schemes rail       │ Colour Schedule (selected scheme)          │
 *   │  • Scheme A  3/16  │  Interior · 8 surfaces                     │
 *   │  • Scheme B  9/16  │    Interior wall  ▮ Linen White · #F1ECDF │
 *   │  + New scheme      │    Ceiling        ▯ Not set  · Assign     │
 *   │                    │    … etc.                                   │
 *   │                    │  Exterior · 8 surfaces                     │
 *   └────────────────────┴────────────────────────────────────────────┘
 *
 * Single-source-of-truth
 * ----------------------
 * The colour picker only offers rows from the live `pro_colours` library
 * (via `/api/pro-colours`). The chosen `code` is sent to the backend, which
 * re-validates against the same library before the row is stored. No
 * client-side hex ever reaches the DB.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Palette,
  Loader2,
  Trash2,
  X,
  Search,
  CheckCircle2,
  Copy,
  History,
  FileText,
  ArrowRight,
  Sparkles,
  Send,
  RotateCcw,
  Columns3,
  Tag as TagIcon,
  Bookmark,
  BookmarkPlus,
  Layers,
  AlertTriangle,
  Share2,
  MessageCircle,
  Link2,
  FileDown,
  Camera,
  Presentation as PresentationIcon,
  ClipboardList,
  Home,
  Clock,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import ExportScheduleDialog from "@/pages/pro/ExportScheduleDialog";
import PresentationBuilder from "@/pages/pro/PresentationBuilder";

// ---------------------------------------------------------------------------
// Client project tabs — Painter-first IA (2026-02 LOCKED)
// ---------------------------------------------------------------------------
// Every client project is organised into 4 canonical tabs so the painter has
// one consistent mental model: Overview · Colour Concepts · Visualisation
// Proposal · Colour Schedule. Tabs live in the URL as `?tab=<key>` so they're
// deep-linkable and back-button friendly.
//
// Photos live in the Rendering Workspace (separate entry point at
// `/pro/renders` → `/workspace`), not inside the CMS project detail — the
// two flows are deliberately decoupled so painters can render dozens of
// photo variations without polluting the client-facing colour concepts.
const CONSULTATION_TABS = [
  { key: "overview",     label: "Overview",              icon: Home,              status: "live" },
  { key: "concepts",     label: "Colour Concepts",       icon: Palette,           status: "live" },
  { key: "presentation", label: "Visualisation Proposal", icon: PresentationIcon, status: "live" },
  { key: "schedule",     label: "Colour Schedule",       icon: ClipboardList,     status: "live" },
];
const TAB_KEYS = CONSULTATION_TABS.map((t) => t.key);

function ConsultationTabs({ activeKey, onChange }) {
  return (
    <nav
      className="border-b border-brand-linen -mx-5 lg:-mx-10 px-5 lg:px-10 overflow-x-auto"
      data-testid="consultation-tabs"
    >
      <div className="flex items-center gap-1 min-w-max">
        {CONSULTATION_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeKey === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onChange(tab.key)}
              data-testid={`consultation-tab-${tab.key}`}
              className={`inline-flex items-center gap-2 h-11 px-4 text-sm border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? "border-brand-teal text-brand-teal font-medium"
                  : "border-transparent text-brand-muted hover:text-brand-ink"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function TabComingSoonPanel({ tab }) {
  const Icon = tab.icon;
  return (
    <div
      className="border border-dashed border-brand-linen bg-white p-10 lg:p-14 text-center"
      data-testid={`consultation-tab-soon-${tab.key}`}
    >
      <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-xl text-brand-ink mb-2">{tab.label}</h3>
      <p className="text-sm text-brand-muted max-w-md mx-auto">
        {tab.soonNote}
      </p>
      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-brand-teal">
        <Clock className="w-3.5 h-3.5" /> Coming soon
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentation Studio · teaser panel (Phase 1 · Founder-defined invitation)
// ---------------------------------------------------------------------------
// This is NOT a static "Coming soon" placeholder. It is deliberately framed
// as an invitation — every painter who lands here should walk away excited
// about what they're about to receive. The Preview Modern Theme button
// opens a lightweight mock preview of the Modern theme so painters can
// see the target output before Phase 5A ships.
const STUDIO_FEATURES = [
  { label: "Before & After comparisons",       icon: Columns3 },
  { label: "Multiple colour concepts",         icon: Palette },
  { label: "Painter-branded presentation",     icon: Sparkles },
  { label: "Scope, investment & terms",        icon: FileDown },
  { label: "Electronic client acceptance",     icon: CheckCircle2 },
  { label: "Interactive web link + PDF",       icon: Share2 },
];

function PresentationStudioTeaser({ project, onOpenStudio }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <div className="space-y-6" data-testid="consultation-tab-panel-presentation">
      {/* Hero card */}
      <div
        className="relative overflow-hidden border border-brand-teal/25 bg-gradient-to-br from-brand-navy via-brand-navy to-brand-teal/40 text-white p-10 lg:p-14"
        data-testid="presentation-studio-hero"
      >
        {/* Ambient grain / spotlight effect */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.35) 0%, transparent 55%)",
        }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-4 px-3 h-7 bg-white/10 border border-white/20 backdrop-blur-sm text-[10px] uppercase tracking-[0.14em]">
            <Sparkles className="w-3 h-3" />
            The signature experience of ColourMatch Pro
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-4">
            Visualisation Proposal Studio
          </h2>
          <p className="text-white/85 text-base max-w-xl leading-relaxed mb-8">
            Turn this client project into a premium visualisation proposal — cover page,
            existing photos, colour concepts, recommended palette, scope, investment and
            terms — delivered as an interactive web link and a branded PDF.
          </p>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl mb-10">
            {STUDIO_FEATURES.map(({ label, icon: FIcon }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-white/90">
                <span className="inline-flex items-center justify-center w-6 h-6 border border-white/30 bg-white/10">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-teal" />
                </span>
                <span className="flex-1">{label}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <button
              onClick={onOpenStudio}
              data-testid="presentation-studio-open"
              className="group inline-flex items-center gap-2 h-12 px-6 bg-white text-brand-navy text-sm font-medium hover:bg-brand-teal hover:text-white transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Open the Proposal Studio
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => setPreviewOpen(true)}
              data-testid="presentation-studio-preview-modern"
              className="inline-flex items-center gap-2 h-12 px-6 border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors"
            >
              <PresentationIcon className="w-4 h-4" />
              Preview Modern Theme
            </button>
          </div>
        </div>
      </div>

      {/* Why it matters strip — subtle painter-first framing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: "Save hours", body: "One client project generates every professional document — no duplicate data entry." },
          { title: "Win more work", body: "Homeowners choose confidently when they see a polished, branded proposal." },
          { title: "Look premium", body: "The Modern theme was designed to feel like it came from a design studio, not a form." },
        ].map((c) => (
          <div key={c.title} className="border border-brand-linen bg-white p-5">
            <div className="text-sm font-medium text-brand-ink mb-1">{c.title}</div>
            <div className="text-xs text-brand-muted leading-relaxed">{c.body}</div>
          </div>
        ))}
      </div>

      {previewOpen && (
        <ModernThemePreviewDialog
          project={project}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

function ModernThemePreviewDialog({ project, onClose }) {
  const painterName = "Your Painting Business";
  const client = project.client_name || "The Miller Residence";
  const site = project.site_address || "42 Ocean Drive";
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden" data-testid="modern-theme-preview-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Modern theme preview</DialogTitle>
          <DialogDescription>
            A sample view of what a homeowner will see when your presentation is rendered in the Modern theme.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pt-6 pb-3 border-b border-brand-linen bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-brand-muted mb-1">Modern theme · Preview</div>
              <h2 className="font-serif text-2xl text-brand-ink">What your client will see</h2>
            </div>
            <div className="text-[10px] uppercase tracking-wide text-brand-teal border border-brand-teal/30 bg-brand-teal/5 px-2 h-6 inline-flex items-center">
              Sample
            </div>
          </div>
        </div>
        {/* Mock presentation mini-preview */}
        <div className="bg-brand-bg p-6 max-h-[65vh] overflow-y-auto" data-testid="modern-theme-preview-body">
          <div className="mx-auto max-w-2xl space-y-4">
            {/* Cover mock */}
            <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-slate-900 via-brand-navy to-slate-800 text-white p-8 flex flex-col justify-between">
              <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                backgroundImage: "linear-gradient(115deg, rgba(255,255,255,0.15) 0%, transparent 45%)",
              }} />
              <div className="relative">
                <div className="text-[9px] uppercase tracking-[0.18em] text-white/70">Client Presentation</div>
                <div className="w-8 h-0.5 bg-brand-teal mt-2 mb-4" />
                <div className="font-serif text-2xl leading-tight">{client}</div>
                <div className="text-white/70 text-xs mt-1">{site}</div>
              </div>
              <div className="relative flex items-end justify-between">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-white/50">Prepared by</div>
                  <div className="font-medium text-sm">{painterName}</div>
                </div>
                <div className="text-[9px] uppercase tracking-wider text-white/50">February 2026</div>
              </div>
            </div>
            {/* Concept board mock */}
            <div className="bg-white border border-brand-linen p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Concept A</div>
                  <div className="font-serif text-xl text-brand-ink">Coastal Calm</div>
                </div>
                <div className="text-[10px] text-brand-muted">3 of 3 concepts</div>
              </div>
              <div className="aspect-[16/9] bg-gradient-to-br from-sky-100 via-slate-100 to-stone-200 mb-4 relative overflow-hidden">
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-stone-300/60 to-transparent" />
                <div className="absolute top-3 left-3 text-[9px] uppercase tracking-wider text-slate-500">Rendered view</div>
              </div>
              <div className="flex items-center gap-2">
                {["#E9E4DA", "#B7C7CE", "#3F5C6C", "#1E2A38", "#F5F1EA"].map((hex) => (
                  <div key={hex} className="flex-1">
                    <div className="h-10 border border-black/5" style={{ background: hex }} />
                    <div className="text-[9px] text-brand-muted mt-1 font-mono truncate">{hex}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-brand-muted mt-4 leading-relaxed">
                Soft coastal whites with slate accents. Designed to feel calm, weathered, and permanent — the palette a coastal architect would specify.
              </div>
            </div>
            {/* Before / after mock */}
            <div className="bg-white border border-brand-linen p-6">
              <div className="text-[10px] uppercase tracking-widest text-brand-muted mb-3">Before / After</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="aspect-[4/3] bg-stone-200 relative">
                  <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider text-stone-500">Before</div>
                </div>
                <div className="aspect-[4/3] bg-gradient-to-br from-sky-100 to-slate-200 relative">
                  <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider text-slate-600">After · Concept A</div>
                </div>
              </div>
            </div>
            {/* Footer strip mock — subtle CM attribution */}
            <div className="flex items-center justify-between text-[10px] pt-2">
              <div className="text-brand-ink font-medium">{painterName}</div>
              <div className="text-brand-muted/70">Generated by ColourMatch Pro</div>
            </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-brand-linen bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="modern-theme-preview-close"
          >
            Close preview
          </Button>
          <Button
            type="button"
            disabled
            className="bg-brand-navy text-white opacity-70 cursor-not-allowed"
            data-testid="modern-theme-preview-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Available in Phase 5A
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverviewPanel({ project, schemesCount, onEditClient, onGoToConcepts }) {
  const info = [
    { label: "Client",          value: project.client_name  || "—" },
    { label: "Email",           value: project.client_email || "—", preserveCase: true },
    { label: "Site address",    value: project.site_address || "—" },
    { label: "Status",          value: (project.status || "draft").replace(/_/g, " ") },
    { label: "Colour concepts", value: `${schemesCount} concept${schemesCount === 1 ? "" : "s"}` },
  ];
  return (
    <div className="space-y-6" data-testid="consultation-tab-panel-overview">
      {/* Client info card */}
      <div className="border border-brand-linen bg-white rounded-2xl p-6 lg:p-8 shadow-soft">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="overline text-brand-teal mb-1">Client project</div>
            <h2 className="font-serif text-2xl text-brand-ink leading-tight truncate">{project.name}</h2>
          </div>
          <button
            onClick={onEditClient}
            data-testid="consultation-overview-edit"
            className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-brand-ink border border-brand-linen bg-white rounded-lg hover:border-brand-teal hover:bg-brand-linen/30 transition-colors shrink-0"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit details
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
          {info.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between border-b border-brand-linen/60 py-2.5 text-sm">
              <dt className="text-brand-muted text-xs uppercase tracking-wider">{row.label}</dt>
              <dd className={`text-brand-ink text-right truncate ml-4 ${row.preserveCase ? "" : "capitalize"}`}>{row.value}</dd>
            </div>
          ))}
        </dl>
        {project.notes && (
          <div className="mt-5 pt-5 border-t border-brand-linen">
            <div className="text-xs text-brand-muted uppercase tracking-wider mb-2">Notes</div>
            <div className="text-sm text-brand-ink whitespace-pre-wrap leading-relaxed">{project.notes}</div>
          </div>
        )}
      </div>

      {/* Primary next-step CTA — the painter should never wonder what to do next */}
      <div className="border border-brand-teal/25 bg-gradient-to-br from-brand-teal/5 to-brand-linen/40 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-teal/15 text-brand-teal flex items-center justify-center flex-shrink-0">
            <Palette className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-brand-ink">
              {schemesCount > 0 ? "Continue building your colour concepts" : "Ready to build your first colour concept?"}
            </div>
            <div className="text-xs text-brand-muted mt-1 leading-relaxed">
              {schemesCount > 0
                ? `${schemesCount} concept${schemesCount === 1 ? "" : "s"} · jump back in and add more, then build a Visualisation Proposal.`
                : "Jump into the Colour Concepts tab to render the first concept for this client."}
            </div>
          </div>
        </div>
        <Button
          onClick={onGoToConcepts}
          className="bg-brand-navy text-white hover:bg-brand-teal transition-colors h-10 px-4 shrink-0 w-full sm:w-auto"
          data-testid="consultation-overview-goto-concepts"
        >
          {schemesCount > 0 ? "Open Colour Concepts" : "Start first concept"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function ProProjectDetail() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TAB_KEYS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "concepts";
  const setTab = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "concepts") {
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const [project, setProject] = useState(null);
  const [schemes, setSchemes] = useState(null);
  const [selectedSchemeId, setSelectedSchemeId] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [creatingScheme, setCreatingScheme] = useState(false);
  // Phase 4 top-level dialogs.
  const [compareOpen, setCompareOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  // Phase 5 — homeowner share modal.
  const [shareOpen, setShareOpen] = useState(false);
  // Phase 7 — inline edit client details (name/email/address).
  const [editClientOpen, setEditClientOpen] = useState(false);

  // Phase 5A — Presentation Studio launch state. When true the Presentation
  // tab renders the full Builder surface; otherwise it renders the teaser
  // invitation.
  const [studioOpen, setStudioOpen] = useState(false);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/pro/cms/projects/${projectId}`);
      setProject(data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(err?.response?.data?.detail) || "Could not load project.");
    }
  }, [projectId]);

  const loadSchemes = useCallback(async () => {
    try {
      const { data } = await api.get(`/pro/cms/projects/${projectId}/schemes`);
      setSchemes(data.items || []);
      setSelectedSchemeId((cur) => cur || (data.items?.[0]?.id ?? null));
    } catch {
      setSchemes([]);
    }
  }, [projectId]);

  useEffect(() => { loadProject(); loadSchemes(); }, [loadProject, loadSchemes]);

  const onSchemeCreated = (s) => {
    setSchemes((prev) => (prev ? [s, ...prev] : [s]));
    setSelectedSchemeId(s.id);
    setCreatingScheme(false);
  };

  const onSchemeDuplicated = (s) => {
    setSchemes((prev) => (prev ? [s, ...prev] : [s]));
    setSelectedSchemeId(s.id);
  };

  const onSchemeUpdated = () => loadSchemes();
  const onSchemeDeleted = (id) => {
    setSchemes((prev) => (prev || []).filter((s) => s.id !== id));
    setSelectedSchemeId((cur) => {
      if (cur !== id) return cur;
      const rest = (schemes || []).filter((s) => s.id !== id);
      return rest[0]?.id || null;
    });
  };

  if (notFound) {
    return (
      <div className="text-center py-20" data-testid="pro-project-detail-not-found">
        <h2 className="font-serif text-2xl text-brand-ink mb-2">Project not found</h2>
        <p className="text-sm text-brand-muted mb-6">It may have been deleted or you don&apos;t have access.</p>
        <Link to="/pro/projects" className="text-brand-teal underline text-sm">Back to Client Projects</Link>
      </div>
    );
  }

  if (!project || schemes === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-brand-muted py-10" data-testid="pro-project-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading consultation…
      </div>
    );
  }

  const tabDef = CONSULTATION_TABS.find((t) => t.key === activeTab) || CONSULTATION_TABS[2];

  return (
    <div className="space-y-6" data-testid="pro-project-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <button
            onClick={() => nav("/pro/projects")}
            data-testid="pro-project-back"
            className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Client projects
          </button>
          <h1 className="font-serif text-3xl text-brand-ink truncate" data-testid="pro-project-name">
            {project.name}
          </h1>
          {(project.client_name || project.client_email || project.site_address) && (
            <div className="text-sm text-brand-muted mt-1 flex flex-wrap items-center gap-x-2">
              {project.client_name && <span>{project.client_name}</span>}
              {project.client_email && (
                <>
                  {project.client_name && <span>·</span>}
                  <span data-testid="pro-project-client-email">{project.client_email}</span>
                </>
              )}
              {project.site_address && (
                <>
                  {(project.client_name || project.client_email) && <span>·</span>}
                  <span>{project.site_address}</span>
                </>
              )}
              <button
                onClick={() => setEditClientOpen(true)}
                data-testid="pro-project-edit-client-btn"
                className="text-xs text-brand-teal underline underline-offset-2 hover:text-brand-teal/80 ml-1"
              >
                Edit
              </button>
            </div>
          )}
          {!project.client_name && !project.client_email && !project.site_address && (
            <button
              onClick={() => setEditClientOpen(true)}
              data-testid="pro-project-add-client-btn"
              className="text-xs text-brand-teal underline underline-offset-2 hover:text-brand-teal/80 mt-1"
            >
              Add client details
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShareOpen(true)}
            data-testid="pro-project-share-btn"
            className="inline-flex items-center gap-1.5 text-xs text-white bg-brand-navy border border-brand-teal px-3 h-8 hover:bg-brand-teal/90 transition-colors"
            title="Share this project with the homeowner"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
          <button
            onClick={() => setCompareOpen(true)}
            disabled={(schemes?.length || 0) < 2}
            data-testid="pro-project-compare-btn"
            className="inline-flex items-center gap-1.5 text-xs text-brand-ink border border-brand-linen bg-white px-3 h-8 hover:border-brand-teal disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={(schemes?.length || 0) < 2 ? "Need at least 2 colour concepts to compare" : "Compare colour concepts side-by-side"}
          >
            <Columns3 className="w-3.5 h-3.5" />
            Compare
          </button>
          <button
            onClick={() => setPresetsOpen(true)}
            data-testid="pro-project-presets-btn"
            className="inline-flex items-center gap-1.5 text-xs text-brand-ink border border-brand-linen bg-white px-3 h-8 hover:border-brand-teal transition-colors"
            title="Manage colour presets"
          >
            <Bookmark className="w-3.5 h-3.5" />
            Presets
          </button>
          <span className="inline-flex items-center px-2 h-6 bg-brand-linen text-brand-ink text-[10px] uppercase tracking-wide">
            {project.status}
          </span>
        </div>
      </div>

      {/* Stage 2B (2026-02) — Source Photo card. Rendered only when the
          Pro project has an attached source_photo. Additional assets (renders,
          proposals, comparisons) will sit alongside this card in later work. */}
      {project.has_source_photo && (
        <div
          data-testid="pro-project-source-photo"
          className="border border-brand-border bg-white rounded-2xl p-4 flex items-center gap-4"
        >
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/pro/cms/projects/${project.id}/photo?v=${project.source_photo_version || 1}`}
            alt="Source photo"
            className="w-24 h-24 rounded-xl object-cover border border-brand-border flex-shrink-0"
            data-testid="pro-project-source-photo-img"
          />
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
              Source Photo
            </div>
            <p className="mt-1 text-sm text-brand-ink font-semibold">
              The canonical image for this client project.
            </p>
            <p className="text-xs text-brand-muted mt-1">
              Renders, colour concepts, and proposals are built from this photo.
            </p>
          </div>
        </div>
      )}

      {/* Client project tab navigation */}
      <ConsultationTabs activeKey={activeTab} onChange={setTab} />

      {/* Body: gated by active tab */}
      {activeTab === "overview" && (
        <OverviewPanel
          project={project}
          schemesCount={schemes?.length || 0}
          onEditClient={() => setEditClientOpen(true)}
          onGoToConcepts={() => setTab("concepts")}
        />
      )}
      {activeTab === "presentation" && (
        studioOpen ? (
          <PresentationBuilder
            consultationId={projectId}
            project={project}
            onBackToTeaser={() => setStudioOpen(false)}
          />
        ) : (
          <PresentationStudioTeaser
            project={project}
            onOpenStudio={() => setStudioOpen(true)}
          />
        )
      )}
      {(activeTab === "concepts" || activeTab === "schedule") && (
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <SchemesRail
          schemes={schemes}
          selectedSchemeId={selectedSchemeId}
          onSelect={setSelectedSchemeId}
          onCreate={() => setCreatingScheme(true)}
          onDuplicated={onSchemeDuplicated}
        />
        <div>
          {selectedSchemeId ? (
            <SchemeDetail
              schemeId={selectedSchemeId}
              onSchemeUpdated={onSchemeUpdated}
              onSchemeDeleted={onSchemeDeleted}
              onSchemeDuplicated={onSchemeDuplicated}
            />
          ) : (
            <NoSchemeSelected onCreate={() => setCreatingScheme(true)} />
          )}
        </div>
      </div>
      )}

      {creatingScheme && (
        <CreateSchemeDialog
          projectId={projectId}
          onClose={() => setCreatingScheme(false)}
          onCreated={onSchemeCreated}
        />
      )}
      {compareOpen && (
        <CompareSchemesDialog
          projectId={projectId}
          schemes={schemes || []}
          onClose={() => setCompareOpen(false)}
          onOpenScheme={(id) => { setSelectedSchemeId(id); setCompareOpen(false); }}
        />
      )}
      {presetsOpen && (
        <PresetsDialog
          onClose={() => setPresetsOpen(false)}
        />
      )}
      {shareOpen && (
        <ShareProjectDialog
          projectId={projectId}
          projectName={project.name}
          onClose={() => setShareOpen(false)}
        />
      )}
      <EditClientDialog
        open={editClientOpen}
        project={project}
        onClose={() => setEditClientOpen(false)}
        onSaved={(next) => { setEditClientOpen(false); setProject(next); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schemes rail (left column)
// ---------------------------------------------------------------------------
function SchemesRail({ schemes, selectedSchemeId, onSelect, onCreate, onDuplicated }) {
  const [dupSource, setDupSource] = useState(null);

  return (
    <aside className="space-y-2" data-testid="pro-schemes-rail">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-brand-muted">Colour Concepts</div>
        <button
          onClick={onCreate}
          data-testid="pro-scheme-new-btn"
          className="inline-flex items-center gap-1 text-xs text-brand-teal hover:underline"
        >
          <Plus className="w-3 h-3" /> New
        </button>
      </div>
      {schemes.length === 0 ? (
        <div className="border border-dashed border-brand-linen p-4 text-xs text-brand-muted text-center">
          No colour concepts yet
        </div>
      ) : (
        <div className="space-y-1">
          {schemes.map((s) => (
            <div
              key={s.id}
              className={`group border transition-colors ${
                selectedSchemeId === s.id
                  ? "border-brand-teal bg-brand-teal/5"
                  : "border-brand-linen bg-white hover:bg-brand-linen/30"
              }`}
              data-testid={`pro-scheme-item-${s.id}`}
            >
              <button
                onClick={() => onSelect(s.id)}
                className="w-full text-left px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-sm text-brand-ink font-medium truncate flex-1">{s.name}</div>
                  {s.status && s.status !== "draft" && (
                    <span className={`shrink-0 text-[9px] uppercase tracking-wide font-medium ${
                      s.status === "approved" ? "text-brand-teal" : "text-brand-ink/70"
                    }`}>{s.status}</span>
                  )}
                </div>
                <div className="text-[11px] text-brand-muted mt-0.5">
                  {s.assigned_surface_count ?? 0} / 16 surfaces
                </div>
              </button>
              <div className="border-t border-brand-linen/70 px-3 py-1.5 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); setDupSource(s); }}
                  data-testid={`pro-scheme-duplicate-${s.id}`}
                  className="inline-flex items-center gap-1 text-[11px] text-brand-muted hover:text-brand-teal"
                  title="Duplicate colour concept"
                >
                  <Copy className="w-3 h-3" /> Duplicate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {dupSource && (
        <DuplicateSchemeDialog
          source={dupSource}
          onClose={() => setDupSource(null)}
          onDuplicated={(s) => { setDupSource(null); onDuplicated?.(s); }}
        />
      )}
    </aside>
  );
}

function NoSchemeSelected({ onCreate }) {
  return (
    <div className="border border-dashed border-brand-linen bg-white p-10 text-center" data-testid="pro-no-scheme-selected">
      <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
        <Palette className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-xl text-brand-ink mb-2">Start your first colour concept</h3>
      <p className="text-sm text-brand-muted max-w-md mx-auto mb-6">
        A colour concept is a reusable colour schedule across the 16 canonical surfaces.
        Every colour applied resolves to an approved ColourMatch library colour.
      </p>
      <Button
        onClick={onCreate}
        className="bg-brand-navy text-white hover:bg-brand-teal/90"
        data-testid="pro-scheme-empty-create"
      >
        <Plus className="w-4 h-4 mr-2" />
        Create colour concept
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheme detail — the colour schedule grid
// ---------------------------------------------------------------------------
function SchemeDetail({ schemeId, onSchemeUpdated, onSchemeDeleted }) {
  const [scheme, setScheme] = useState(null);
  const [error, setError] = useState(null);
  const [assigningSlug, setAssigningSlug] = useState(null);
  const [saveAsPresetOpen, setSaveAsPresetOpen] = useState(false);
  const [applyPresetOpen, setApplyPresetOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pro/cms/schemes/${schemeId}`);
      setScheme(data);
      setError(null);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Could not load scheme.");
      setScheme(null);
    }
  }, [schemeId]);

  useEffect(() => { load(); }, [load]);

  const deleteScheme = async () => {
    if (!window.confirm("Delete this scheme? All colour assignments will be lost.")) return;
    try {
      await api.delete(`/pro/cms/schemes/${schemeId}`);
      toast.success("Scheme deleted");
      onSchemeDeleted?.(schemeId);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed.");
    }
  };

  const onAssigned = async (slug, next) => {
    // Optimistic update
    setScheme((prev) => {
      if (!prev) return prev;
      const surfaces = prev.surfaces.map((s) =>
        s.surface_slug === slug
          ? { ...s, ...next, assigned: true, colour_stale: false }
          : s
      );
      const assigned = surfaces.filter((s) => s.assigned).length;
      return { ...prev, surfaces, assigned_surface_count: assigned };
    });
    setAssigningSlug(null);
    onSchemeUpdated?.();
  };

  const onUnassigned = async (slug) => {
    setScheme((prev) => {
      if (!prev) return prev;
      const surfaces = prev.surfaces.map((s) =>
        s.surface_slug === slug
          ? { ...s, colour_code: null, colour_name: null, colour_hex: null, colour_family: null, notes: null, assigned: false, colour_stale: false }
          : s
      );
      const assigned = surfaces.filter((s) => s.assigned).length;
      return { ...prev, surfaces, assigned_surface_count: assigned };
    });
    onSchemeUpdated?.();
  };

  if (error) {
    return <div className="border border-yellow-300 bg-yellow-50 text-sm text-yellow-800 px-4 py-3">{error}</div>;
  }
  if (!scheme) {
    return <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin" /> Loading scheme…</div>;
  }

  const interior = scheme.surfaces.filter((s) => s.category === "interior");
  const exterior = scheme.surfaces.filter((s) => s.category === "exterior");

  return (
    <div className="space-y-6" data-testid="pro-scheme-detail">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-2xl text-brand-ink truncate">{scheme.name}</h2>
          {scheme.description && <p className="text-sm text-brand-muted mt-1">{scheme.description}</p>}
          <div className="text-xs text-brand-muted mt-2" data-testid="pro-scheme-progress">
            {scheme.assigned_surface_count} / {scheme.total_surface_count} surfaces assigned
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusControls scheme={scheme} onChanged={load} />
          <button
            onClick={() => setApplyPresetOpen(true)}
            data-testid="pro-scheme-apply-preset-btn"
            className="inline-flex items-center gap-1 text-xs text-brand-ink hover:text-brand-teal border border-brand-linen bg-white px-2.5 h-7 transition-colors"
            title="Apply a saved preset to this scheme"
          >
            <Layers className="w-3 h-3" />
            Apply preset
          </button>
          <button
            onClick={() => setSaveAsPresetOpen(true)}
            disabled={(scheme.assigned_surface_count || 0) === 0}
            data-testid="pro-scheme-save-preset-btn"
            className="inline-flex items-center gap-1 text-xs text-brand-ink hover:text-brand-teal border border-brand-linen bg-white px-2.5 h-7 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={(scheme.assigned_surface_count || 0) === 0 ? "Assign at least one colour to save as a preset" : "Save this scheme as a reusable preset"}
          >
            <BookmarkPlus className="w-3 h-3" />
            Save as preset
          </button>
          <button
            onClick={() => setExportOpen(true)}
            data-testid="pro-scheme-export-pdf-btn"
            className="inline-flex items-center gap-1 text-xs text-brand-ink hover:text-brand-teal border border-brand-linen bg-white px-2.5 h-7 transition-colors"
            title="Export this scheme as a professional PDF colour schedule"
          >
            <FileDown className="w-3 h-3" />
            Export PDF
          </button>
          <button
            onClick={deleteScheme}
            data-testid="pro-scheme-delete-btn"
            className="text-brand-muted hover:text-red-600 transition-colors"
            title="Delete colour concept"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <SurfaceGroup
        title="Interior"
        surfaces={interior}
        onAssignClick={setAssigningSlug}
        schemeId={schemeId}
        onUnassigned={onUnassigned}
      />
      <SurfaceGroup
        title="Exterior"
        surfaces={exterior}
        onAssignClick={setAssigningSlug}
        schemeId={schemeId}
        onUnassigned={onUnassigned}
      />

      <RevisionPanel schemeId={schemeId} onNoteAdded={load} onRestored={load} />

      {assigningSlug && (
        <AssignColourDialog
          schemeId={schemeId}
          surface={scheme.surfaces.find((s) => s.surface_slug === assigningSlug)}
          onClose={() => setAssigningSlug(null)}
          onAssigned={(next) => onAssigned(assigningSlug, next)}
        />
      )}
      {saveAsPresetOpen && (
        <SaveAsPresetDialog
          scheme={scheme}
          onClose={() => setSaveAsPresetOpen(false)}
        />
      )}
      {applyPresetOpen && (
        <ApplyPresetDialog
          schemeId={schemeId}
          onClose={() => setApplyPresetOpen(false)}
          onApplied={() => { setApplyPresetOpen(false); load(); onSchemeUpdated?.(); }}
        />
      )}
      <ExportScheduleDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        scheme={scheme}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status controls — state-machine transitions with optional painter note
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  draft:     { label: "Draft",     chip: "bg-brand-linen text-brand-ink" },
  presented: { label: "Presented", chip: "bg-brand-teal/10 text-brand-teal" },
  approved:  { label: "Approved",  chip: "bg-brand-navy text-white" },
};
const STATUS_TRANSITIONS = {
  draft:     [{ next: "presented", label: "Mark as presented", icon: Send }],
  presented: [
    { next: "approved", label: "Mark as approved", icon: CheckCircle2 },
    { next: "draft", label: "Send back to draft", icon: RotateCcw },
  ],
  approved:  [{ next: "presented", label: "Unlock (back to presented)", icon: RotateCcw }],
};

function StatusControls({ scheme, onChanged }) {
  const [pending, setPending] = useState(null);
  const style = STATUS_STYLES[scheme.status] || STATUS_STYLES.draft;
  const transitions = STATUS_TRANSITIONS[scheme.status] || [];
  return (
    <div className="flex items-center gap-2" data-testid="pro-scheme-status-controls">
      <span
        className={`inline-flex items-center px-2.5 h-6 text-[10px] uppercase tracking-wide font-medium ${style.chip}`}
        data-testid="pro-scheme-status-chip"
      >
        {style.label}
      </span>
      {transitions.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.next}
            onClick={() => setPending(t)}
            data-testid={`pro-status-to-${t.next}`}
            className="inline-flex items-center gap-1 text-xs text-brand-teal hover:underline"
          >
            <Icon className="w-3 h-3" /> {t.label}
          </button>
        );
      })}
      {pending && (
        <StatusChangeDialog
          scheme={scheme}
          next={pending}
          onClose={() => setPending(null)}
          onDone={() => { setPending(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function StatusChangeDialog({ scheme, next, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/pro/cms/schemes/${scheme.id}/status`, {
        status: next.next,
        painter_note: note.trim() || null,
      });
      toast.success(`Marked as ${next.next}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Status change failed.");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-status-change-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">{next.label}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-4">
            Add an optional note that will appear in the revision history.
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Version presented to client at 3pm site meeting"
            rows={3}
            data-testid="pro-status-note"
            autoFocus
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="pro-status-submit">
              {busy ? "Applying…" : next.label}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Revision history panel — append-only timeline with painter-note input
// ---------------------------------------------------------------------------
const EVENT_META = {
  created:            { icon: Sparkles,     label: "Created" },
  duplicated:         { icon: Copy,         label: "Duplicated" },
  scheme_edited:      { icon: FileText,     label: "Edited" },
  surface_assigned:   { icon: Palette,      label: "Colour assigned" },
  surface_unassigned: { icon: X,            label: "Colour removed" },
  status_changed:     { icon: ArrowRight,   label: "Status changed" },
  milestone_note:     { icon: FileText,     label: "Note" },
  restored:           { icon: RotateCcw,    label: "Restored" },
  tag_added:          { icon: TagIcon,      label: "Tagged" },
  tag_removed:        { icon: TagIcon,      label: "Untagged" },
  preset_applied:     { icon: Layers,       label: "Preset applied" },
};

/**
 * Renders the append-only revision timeline. Every row is IMMUTABLE:
 * no edit button, no delete button — ever. Historical revisions can
 * only be READ or RESTORED (which appends a new revision). This mirrors
 * the permanent architectural rule enforced server-side in
 * `/app/backend/models/pro/cms.py`.
 */
function RevisionPanel({ schemeId, onNoteAdded, onRestored }) {
  const [items, setItems] = useState(null);
  const [tags, setTags] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [revsResp, tagsResp] = await Promise.all([
        api.get(`/pro/cms/schemes/${schemeId}/revisions`),
        api.get(`/pro/cms/schemes/${schemeId}/tags`),
      ]);
      setItems(revsResp.data.items || []);
      setCurrentStatus(revsResp.data.current_status || null);
      setTags(tagsResp.data.items || []);
    } catch {
      setItems([]);
      setTags([]);
    }
  }, [schemeId]);

  useEffect(() => { load(); }, [load]);

  // Group tags by revision_number for quick lookup in the timeline.
  const tagsByRev = useMemo(() => {
    const map = new Map();
    for (const t of tags) {
      const arr = map.get(t.revision_number) || [];
      arr.push(t);
      map.set(t.revision_number, arr);
    }
    return map;
  }, [tags]);

  const removeTag = async (tagId) => {
    try {
      await api.delete(`/pro/cms/schemes/${schemeId}/tags/${tagId}`);
      toast.success("Tag removed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not remove tag.");
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    const text = note.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/pro/cms/schemes/${schemeId}/revisions/note`, { painter_note: text });
      setNote("");
      toast.success("Note added to timeline");
      await load();
      onNoteAdded?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not add note.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="pro-revision-panel" className="space-y-3">
      <div className="flex items-center gap-2 mt-8">
        <History className="w-4 h-4 text-brand-muted" />
        <div className="overline text-brand-muted">Revision history</div>
        <span className="text-[10px] text-brand-muted italic ml-auto">Append-only · nothing is ever edited or deleted</span>
      </div>

      <form onSubmit={submitNote} className="border border-brand-linen bg-white p-3 space-y-2">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note — e.g. Customer requested darker fascia · Updated after site meeting"
          rows={2}
          data-testid="pro-revision-note-input"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={!note.trim() || busy}
            className="bg-brand-navy text-white hover:bg-brand-teal/90"
            data-testid="pro-revision-note-submit"
          >
            {busy ? "Adding…" : "Add to timeline"}
          </Button>
        </div>
      </form>

      {items === null ? (
        <div className="text-xs text-brand-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading history…
        </div>
      ) : items.length === 0 ? (
        <div className="text-xs text-brand-muted p-4 border border-dashed border-brand-linen text-center">
          No history yet.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="pro-revision-list">
          {items.map((r, idx) => {
            const meta = EVENT_META[r.event_type] || { icon: FileText, label: r.event_type };
            const Icon = meta.icon;
            const isLatest = idx === 0;
            // Restoring is only meaningful for revisions that captured a
            // snapshot with any assignments — created/milestone_note/etc.
            // rarely make sense as restore targets, but we allow any
            // revision except the current latest (that's a no-op).
            const canRestore = !isLatest && r.event_type !== "milestone_note";
            const canTag = !["tag_added", "tag_removed"].includes(r.event_type);
            const rowTags = tagsByRev.get(r.revision_number) || [];
            const dt = new Date(r.created_at);
            return (
              <li
                key={r.id}
                data-testid={`pro-revision-${r.revision_number}`}
                className="group flex gap-3 p-3 border border-brand-linen bg-white"
              >
                <div className="w-6 h-6 rounded-full bg-brand-linen/60 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-3 h-3 text-brand-ink" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide font-medium text-brand-muted">
                      Rev #{r.revision_number}
                    </span>
                    <span className="text-sm text-brand-ink font-medium flex-1 min-w-0">{r.event_summary}</span>
                    {r.event_type === "status_changed" && r.to_status && (
                      <span className={`text-[9px] uppercase tracking-wide font-medium ${
                        r.to_status === "approved" ? "text-brand-teal" : "text-brand-ink/70"
                      }`}>{r.to_status}</span>
                    )}
                    {r.restored_from_revision_number && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-brand-teal bg-brand-teal/10 px-1.5 h-4"
                        data-testid={`pro-revision-restored-badge-${r.revision_number}`}
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                        Restored from Rev {r.restored_from_revision_number}
                      </span>
                    )}
                  </div>
                  {/* Version tag chips */}
                  {rowTags.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`pro-revision-tags-${r.revision_number}`}>
                      {rowTags.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-brand-ink/5 text-brand-ink px-1.5 h-5"
                          data-testid={`pro-tag-chip-${t.id}`}
                        >
                          <TagIcon className="w-2.5 h-2.5" />
                          {t.label}
                          <button
                            onClick={() => removeTag(t.id)}
                            data-testid={`pro-tag-remove-${t.id}`}
                            className="ml-0.5 text-brand-muted hover:text-red-600 transition-colors"
                            title="Remove tag"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {r.painter_note && (
                    <div className={`text-xs mt-1 ${r.event_type === "milestone_note" ? "text-brand-ink/80" : "text-brand-ink/80 italic"}`}>
                      &ldquo;{r.painter_note}&rdquo;
                    </div>
                  )}
                  <div className="text-[10px] text-brand-muted mt-1 flex items-center gap-2">
                    <span>{dt.toLocaleString()}</span>
                    {r.created_by_name && (
                      <>
                        <span>·</span>
                        <span>{r.created_by_name}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="shrink-0 self-start flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canTag && (
                    <button
                      onClick={() => setTagTarget(r)}
                      data-testid={`pro-revision-tag-btn-${r.revision_number}`}
                      className="inline-flex items-center gap-1 text-[11px] text-brand-ink hover:text-brand-teal"
                      title="Add a version tag to this revision"
                    >
                      <TagIcon className="w-3 h-3" /> Tag
                    </button>
                  )}
                  {canRestore && (
                    <button
                      onClick={() => setRestoreTarget(r)}
                      data-testid={`pro-revision-restore-${r.revision_number}`}
                      className="inline-flex items-center gap-1 text-[11px] text-brand-teal hover:underline"
                      title="Restore this version — creates a new revision, never overwrites"
                    >
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {restoreTarget && (
        <RestoreRevisionDialog
          schemeId={schemeId}
          revision={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onDone={async () => {
            setRestoreTarget(null);
            await load();
            onRestored?.();
          }}
        />
      )}
      {tagTarget && (
        <AddTagDialog
          schemeId={schemeId}
          revision={tagTarget}
          existingLabels={tags.map((t) => t.label.toLowerCase())}
          onClose={() => setTagTarget(null)}
          onDone={async () => { setTagTarget(null); await load(); }}
        />
      )}
    </section>
  );
}

function RestoreRevisionDialog({ schemeId, revision, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(
        `/pro/cms/schemes/${schemeId}/revisions/${revision.revision_number}/restore`,
        { painter_note: note.trim() || null },
      );
      const dropped = data.dropped_surfaces?.length || 0;
      toast.success(
        `Restored from Rev ${revision.revision_number}`
        + (dropped ? ` · ${dropped} surface(s) skipped (colour no longer in library)` : "")
      );
      onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Restore failed.");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-restore-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Restore Revision {revision.revision_number}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-3">
            This appends a <strong>new revision</strong> to the timeline using the colour schedule from Rev {revision.revision_number}. The historical revision is not modified. Nothing is lost — the current schedule joins the history and can be restored from later if the client changes their mind.
          </div>
          <div className="border border-brand-linen bg-brand-linen/20 p-2 text-[11px] text-brand-ink mb-3">
            <div className="font-medium">Rev #{revision.revision_number} · {revision.event_summary}</div>
            {revision.painter_note && <div className="italic mt-1">&ldquo;{revision.painter_note}&rdquo;</div>}
            <div className="text-brand-muted mt-1">{new Date(revision.created_at).toLocaleString()}</div>
          </div>
          <Label className="text-brand-ink">Painter note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Client wanted the grey walls back after all"
            rows={2}
            data-testid="pro-restore-note"
            className="mt-1.5"
            autoFocus
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-restore-submit"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {busy ? "Restoring…" : `Restore Rev ${revision.revision_number}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Duplicate-scheme dialog
// ---------------------------------------------------------------------------
function DuplicateSchemeDialog({ source, onClose, onDuplicated }) {
  const [name, setName] = useState(""); // empty → server auto-names
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/cms/schemes/${source.id}/duplicate`, {
        name: name.trim() || null,
        painter_note: note.trim() || null,
      });
      toast.success(`Duplicated as ${data.name}`);
      onDuplicated(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Duplicate failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-duplicate-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Duplicate colour concept</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-4">
            Duplicating <strong className="text-brand-ink">{source.name}</strong>. The copy starts as a <strong>draft</strong> with its own revision history — every assigned surface colour and note is copied over.
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-brand-ink">New name (optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`${source.name} · Copy`}
                data-testid="pro-duplicate-name"
                autoFocus
                className="mt-1.5"
              />
              <p className="text-[11px] text-brand-muted mt-1">Leave blank to auto-name.</p>
            </div>
            <div>
              <Label className="text-brand-ink">Painter note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Warmer variant for option 2"
                rows={2}
                data-testid="pro-duplicate-note"
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="pro-duplicate-submit">
              {busy ? "Duplicating…" : "Duplicate colour concept"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SurfaceGroup({ title, surfaces, onAssignClick, schemeId, onUnassigned }) {
  return (
    <section>
      <div className="overline text-brand-muted mb-3">{title} · {surfaces.length} surfaces</div>
      <div className="border border-brand-linen bg-white divide-y divide-brand-linen">
        {surfaces.map((s) => (
          <SurfaceRow
            key={s.surface_slug}
            surface={s}
            schemeId={schemeId}
            onAssignClick={() => onAssignClick(s.surface_slug)}
            onUnassigned={() => onUnassigned(s.surface_slug)}
          />
        ))}
      </div>
    </section>
  );
}

function SurfaceRow({ surface, schemeId, onAssignClick, onUnassigned }) {
  const [tradeOpen, setTradeOpen] = useState(false);

  const unassign = async () => {
    try {
      await api.delete(`/pro/cms/schemes/${schemeId}/surfaces/${surface.surface_slug}`);
      onUnassigned();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Unassign failed.");
    }
  };

  return (
    <div
      className="flex items-center gap-4 p-4 hover:bg-brand-linen/20 transition-colors"
      data-testid={`surface-row-${surface.surface_slug}`}
    >
      {/* Swatch OR placeholder */}
      <div className="flex-shrink-0">
        {surface.assigned && surface.colour_hex ? (
          <div
            className="w-12 h-12 border border-brand-linen shadow-sm"
            style={{ background: surface.colour_hex }}
            title={`${surface.colour_name} · ${surface.colour_hex}`}
            data-testid={`surface-swatch-${surface.surface_slug}`}
          />
        ) : surface.colour_stale ? (
          <div
            className="w-12 h-12 border-2 border-dashed border-yellow-400 bg-yellow-50 flex items-center justify-center text-[10px] text-yellow-800 text-center leading-tight"
            title="Assigned colour is no longer in the ColourMatch library. Reassign to fix."
          >
            Stale
          </div>
        ) : (
          <div className="w-12 h-12 border-2 border-dashed border-brand-linen bg-brand-linen/20" />
        )}
      </div>

      {/* Surface + colour label */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-brand-ink">{surface.surface_display_name}</div>
        {surface.assigned && !surface.colour_stale ? (
          <div className="text-xs text-brand-muted mt-0.5">
            <span className="text-brand-ink">{surface.colour_name}</span>
            <span className="mx-1.5">·</span>
            <span className="font-mono">{surface.colour_hex}</span>
            {surface.colour_family && <><span className="mx-1.5">·</span>{surface.colour_family}</>}
          </div>
        ) : surface.colour_stale ? (
          <div className="text-xs text-yellow-700 mt-0.5">
            Previous colour <code>{surface.colour_code}</code> is no longer in the library — please reassign.
          </div>
        ) : (
          <div className="text-xs text-brand-muted mt-0.5">Not set</div>
        )}
        {surface.preferred_trade_ref && (
          <div className="text-[10px] text-brand-muted mt-1 flex items-center gap-1 flex-wrap" data-testid={`surface-trade-ref-${surface.surface_slug}`}>
            <span className="inline-flex items-center gap-1 bg-brand-linen/50 text-brand-ink px-1.5 h-4">
              via {surface.preferred_trade_ref.brand} {surface.preferred_trade_ref.trade_name}
            </span>
          </div>
        )}
        {surface.notes && (
          <div className="text-[11px] text-brand-muted mt-1 italic truncate">Note: {surface.notes}</div>
        )}
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {surface.assigned && !surface.colour_stale && (
          <button
            onClick={() => setTradeOpen(true)}
            data-testid={`surface-trade-equivalents-${surface.surface_slug}`}
            className="inline-flex items-center gap-1 text-[11px] text-brand-ink border border-brand-linen bg-white px-2 h-7 hover:border-brand-teal hover:text-brand-teal transition-colors"
            title="See equivalent trade colours"
          >
            <Search className="w-3 h-3" />
            Trade
          </button>
        )}
        <Button
          size="sm"
          variant={surface.assigned ? "outline" : "default"}
          onClick={onAssignClick}
          data-testid={`surface-assign-${surface.surface_slug}`}
          className={surface.assigned ? "" : "bg-brand-navy text-white hover:bg-brand-teal/90"}
        >
          {surface.assigned ? "Change" : "Assign"}
        </Button>
        {surface.assigned && (
          <button
            onClick={unassign}
            data-testid={`surface-unassign-${surface.surface_slug}`}
            className="text-brand-muted hover:text-red-600 transition-colors"
            title="Remove colour"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {tradeOpen && (
        <TradeEquivalentsDialog
          colourCode={surface.colour_code}
          colourName={surface.colour_name}
          colourHex={surface.colour_hex}
          onClose={() => setTradeOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 6 · Trade Equivalents dialog (reverse lookup)
// ---------------------------------------------------------------------------
function TradeEquivalentsDialog({ colourCode, colourName, colourHex, onClose }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/pro/cms/library-code/${encodeURIComponent(colourCode)}/equivalents`);
        setState({ loading: false, data, error: null });
      } catch (e) {
        setState({ loading: false, data: null, error: formatApiError(e?.response?.data?.detail) || "Could not load equivalents." });
      }
    })();
  }, [colourCode]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch { /* ignore */ }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="pro-trade-equivalents-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Trade equivalents</DialogTitle>
          <DialogDescription className="sr-only">
            Equivalent brand paints for this ColourMatch library colour, provided as purchasing references.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3 flex items-center gap-3 border border-brand-linen bg-brand-linen/20 p-3">
          <div className="w-10 h-10 border border-brand-linen shrink-0" style={{ background: colourHex }} />
          <div className="min-w-0">
            <div className="text-sm text-brand-ink font-medium">{colourName}</div>
            <div className="text-[11px] text-brand-muted font-mono">{colourHex} · {colourCode}</div>
          </div>
        </div>
        <div className="text-[11px] text-brand-muted mt-2">
          These trade paint names are provided as purchasing references. Your approved ColourMatch colour remains the official project colour.
        </div>
        <div className="flex-1 overflow-auto border border-brand-linen bg-white mt-3">
          {state.loading ? (
            <div className="p-4 text-sm text-brand-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading equivalents…
            </div>
          ) : state.error ? (
            <div className="p-4 text-sm text-yellow-800 bg-yellow-50">{state.error}</div>
          ) : !state.data || state.data.total === 0 ? (
            <div className="p-4 text-sm text-brand-muted text-center">
              No trade equivalents recorded yet for this colour.
            </div>
          ) : (
            <ul className="divide-y divide-brand-linen">
              {state.data.brands.map((b) => (
                <li key={b.brand} className="p-3" data-testid={`pro-trade-brand-${b.brand}`}>
                  <div className="text-[10px] uppercase tracking-wide text-brand-muted font-medium mb-1.5">
                    {b.brand} · {b.count}
                  </div>
                  <ul className="space-y-1">
                    {b.items.map((it) => (
                      <li key={it.id} className="flex items-center gap-2 text-xs">
                        <div className="w-4 h-4 border border-brand-linen shrink-0" style={{ background: it.ref_hex }} />
                        <span className="text-brand-ink flex-1 min-w-0 truncate">{it.trade_name}</span>
                        <span className="font-mono text-brand-muted">{it.ref_hex}</span>
                        {typeof it.delta_e === "number" && (
                          <span className="text-[10px] text-brand-muted" title="CIE distance from the ColourMatch colour">Δe {it.delta_e.toFixed(1)}</span>
                        )}
                        <button
                          onClick={() => copy(`${b.brand} ${it.trade_name}`)}
                          data-testid={`pro-trade-copy-${it.id}`}
                          className="text-brand-muted hover:text-brand-teal text-[10px] uppercase tracking-wide"
                          title="Copy brand + trade name"
                        >
                          Copy
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Create-scheme dialog
// ---------------------------------------------------------------------------
function CreateSchemeDialog({ projectId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/cms/projects/${projectId}/schemes`, {
        name: name.trim(),
        description: description.trim() || null,
      });
      toast.success("Scheme created");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Create failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-create-scheme-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Create colour concept</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-brand-ink">Colour concept name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Coastal Calm" autoFocus className="mt-1.5" data-testid="pro-create-scheme-name" />
            </div>
            <div>
              <Label className="text-brand-ink">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional — a short description of the colour concept intent" rows={2} className="mt-1.5" data-testid="pro-create-scheme-description" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy || !name.trim()} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="pro-create-scheme-submit">
              {busy ? "Creating…" : "Create colour concept"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Assign-colour dialog — SSOT enforcement point on the client.
// Only offers rows from the live `pro_colours` library.
// ---------------------------------------------------------------------------
function AssignColourDialog({ schemeId, surface, onClose, onAssigned }) {
  // Phase 6 · two modes:
  //  - "library" (default) → paint the SSOT library colour directly
  //  - "trade"             → search by brand + trade name; server resolves
  //                          to a ColourMatch code and stamps the trade
  //                          reference for purchasing.
  const [mode, setMode] = useState("library");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState(null);
  const [family, setFamily] = useState("");
  const [pick, setPick] = useState(null);
  const [notes, setNotes] = useState(surface.notes || "");
  const [busy, setBusy] = useState(false);
  // Trade-search state
  const [tradeBrand, setTradeBrand] = useState("");
  const [tradeQuery, setTradeQuery] = useState("");
  const [tradeResults, setTradeResults] = useState(null);
  const [tradePick, setTradePick] = useState(null);

  useEffect(() => {
    api.get("/pro-colours/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Debounce search-as-you-type against /api/pro-colours
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/pro-colours", {
          params: {
            q: q || undefined,
            family: family || undefined,
            page: 1,
            page_size: 36,
            sort: "popularity",
          },
        });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch {
        setItems([]);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [q, family]);

  const submit = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { data } = await api.put(
        `/pro/cms/schemes/${schemeId}/surfaces/${surface.surface_slug}`,
        { colour_code: pick.code, notes: notes.trim() || null },
      );
      toast.success(`${pick.name} applied to ${surface.surface_display_name}`);
      onAssigned(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Assign failed.");
      setBusy(false);
    }
  };

  // Phase 6 · debounce a call to the alias search when in "trade" mode.
  useEffect(() => {
    if (mode !== "trade") return;
    if (!tradeQuery.trim() || tradeQuery.trim().length < 2) {
      setTradeResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/paint-aliases/search", {
          params: { q: tradeQuery, limit: 12 },
        });
        let rows = data.results || [];
        if (tradeBrand) {
          rows = rows.filter((r) => (r.brand || "").toLowerCase() === tradeBrand.toLowerCase());
        }
        setTradeResults(rows);
      } catch {
        setTradeResults([]);
      }
    }, 260);
    return () => clearTimeout(handle);
  }, [mode, tradeQuery, tradeBrand]);

  const submitTrade = async () => {
    if (!tradePick) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/pro/cms/schemes/${schemeId}/surfaces/${surface.surface_slug}/assign-by-trade`,
        {
          brand: tradePick.brand,
          trade_name: tradePick.trade_name,
          notes: notes.trim() || null,
        },
      );
      toast.success(
        `${tradePick.brand} ${tradePick.trade_name} → ${data.colour_name} applied to ${surface.surface_display_name}`
      );
      onAssigned(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Assign failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="pro-assign-colour-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">
            Assign colour to {surface.surface_display_name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Assign a ColourMatch library colour to this surface, or search by a trade paint name.
          </DialogDescription>
        </DialogHeader>

        {/* Phase 6 · mode tabs */}
        <div className="mt-3 grid grid-cols-2 gap-2 bg-brand-linen/20 p-1" data-testid="pro-assign-mode-tabs">
          <button
            type="button"
            onClick={() => setMode("library")}
            data-testid="pro-assign-mode-library"
            className={`text-xs h-8 px-3 transition-colors ${
              mode === "library"
                ? "bg-white text-brand-ink font-medium border border-brand-linen"
                : "text-brand-muted hover:text-brand-ink"
            }`}
          >
            <Palette className="w-3.5 h-3.5 inline mr-1.5" />
            By ColourMatch
          </button>
          <button
            type="button"
            onClick={() => setMode("trade")}
            data-testid="pro-assign-mode-trade"
            className={`text-xs h-8 px-3 transition-colors ${
              mode === "trade"
                ? "bg-white text-brand-ink font-medium border border-brand-linen"
                : "text-brand-muted hover:text-brand-ink"
            }`}
          >
            <Search className="w-3.5 h-3.5 inline mr-1.5" />
            By trade name
          </button>
        </div>

        {mode === "library" ? (
          <>
        {/* Search + family filter */}
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search ColourMatch library…"
              className="pl-9"
              data-testid="pro-assign-search"
              autoFocus
            />
          </div>
          <select
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            data-testid="pro-assign-family-filter"
            className="border border-brand-linen bg-white px-3 py-2 text-sm"
          >
            <option value="">All families</option>
            {(meta?.families || []).map((f) => (
              <option key={f.family} value={f.family}>{f.family} ({f.count})</option>
            ))}
          </select>
        </div>

        {/* Swatch grid */}
        <div className="mt-3 border border-brand-linen bg-white max-h-[360px] overflow-y-auto" data-testid="pro-assign-grid">
          {items === null ? (
            <div className="p-6 text-sm text-brand-muted">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-brand-muted">No colours match this filter.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 p-3">
              {items.map((c) => {
                const selected = pick?.code === c.code;
                return (
                  <button
                    key={c.code}
                    onClick={() => setPick(c)}
                    data-testid={`pro-assign-swatch-${c.code}`}
                    className={`text-left p-1.5 border transition-colors ${
                      selected
                        ? "border-brand-teal ring-2 ring-brand-teal/30"
                        : "border-brand-linen hover:border-brand-ink"
                    }`}
                  >
                    <div className="w-full aspect-square mb-1 border border-brand-linen" style={{ background: c.hex }} />
                    <div className="text-[11px] text-brand-ink font-medium truncate">{c.name}</div>
                    <div className="text-[10px] text-brand-muted font-mono">{c.hex}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-[11px] text-brand-muted mt-1">
          {total.toLocaleString()} colours in the ColourMatch library. Every applied colour resolves to an approved library row.
        </div>

        {/* Pick summary + notes */}
        {pick && (
          <div className="mt-3 border border-brand-teal/40 bg-brand-teal/5 p-3 flex items-start gap-3" data-testid="pro-assign-selected">
            <div className="w-12 h-12 border border-brand-linen flex-shrink-0" style={{ background: pick.hex }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-brand-ink">{pick.name}</div>
              <div className="text-xs text-brand-muted font-mono">{pick.hex} · {pick.code}</div>
              <div className="mt-2">
                <Label className="text-brand-ink text-xs">Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. two coats · low sheen"
                  className="mt-1 text-xs"
                  data-testid="pro-assign-notes"
                />
              </div>
            </div>
            <CheckCircle2 className="w-5 h-5 text-brand-teal flex-shrink-0" />
          </div>
        )}
          </>
        ) : (
          <TradeAssignPane
            tradeBrand={tradeBrand}
            setTradeBrand={setTradeBrand}
            tradeQuery={tradeQuery}
            setTradeQuery={setTradeQuery}
            tradeResults={tradeResults}
            tradePick={tradePick}
            setTradePick={setTradePick}
            notes={notes}
            setNotes={setNotes}
          />
        )}

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          {mode === "library" ? (
            <Button
              type="button"
              onClick={submit}
              disabled={!pick || busy}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-assign-submit"
            >
              {busy ? "Assigning…" : pick ? `Assign ${pick.name}` : "Pick a colour"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submitTrade}
              disabled={!tradePick || busy}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-assign-trade-submit"
            >
              {busy ? "Assigning…" : tradePick ? `Assign via ${tradePick.brand}` : "Pick a trade colour"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Phase 6 · Trade-name search pane (inner body of AssignColourDialog)
// ---------------------------------------------------------------------------
function TradeAssignPane({
  tradeBrand, setTradeBrand,
  tradeQuery, setTradeQuery,
  tradeResults, tradePick, setTradePick,
  notes, setNotes,
}) {
  return (
    <div className="mt-2 space-y-3" data-testid="pro-assign-trade-pane">
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2">
        <Input
          list="pro-assign-brand-list"
          value={tradeBrand}
          onChange={(e) => setTradeBrand(e.target.value)}
          placeholder="Brand (optional)"
          maxLength={64}
          data-testid="pro-assign-trade-brand"
        />
        <datalist id="pro-assign-brand-list">
          {["Resene", "Dulux", "Taubmans", "Wattyl", "Haymes", "British Paints", "Porter's", "Solver", "Berger"].map((b) => (
            <option key={b} value={b} />
          ))}
        </datalist>
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input
            value={tradeQuery}
            onChange={(e) => setTradeQuery(e.target.value)}
            placeholder="Trade name — e.g. Spanish White"
            className="pl-9"
            data-testid="pro-assign-trade-query"
            autoFocus
          />
        </div>
      </div>

      <div className="text-[11px] text-brand-muted -mt-1">
        We&apos;ll find the closest ColourMatch library colour and render using that. Your trade reference is kept alongside it for purchasing only.
      </div>

      <div className="border border-brand-linen bg-white max-h-[300px] overflow-y-auto" data-testid="pro-assign-trade-results">
        {tradeQuery.trim().length < 2 ? (
          <div className="p-4 text-sm text-brand-muted">Start typing a trade name to search…</div>
        ) : tradeResults === null ? (
          <div className="p-4 text-sm text-brand-muted flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching…
          </div>
        ) : tradeResults.length === 0 ? (
          <div className="p-4 text-sm text-brand-muted">
            No matches found. Try a different brand or wording — or switch to &ldquo;By ColourMatch&rdquo; and assign directly.
          </div>
        ) : (
          <ul className="divide-y divide-brand-linen">
            {tradeResults.map((r) => {
              const on = tradePick && tradePick.brand === r.brand && tradePick.trade_name === r.trade_name;
              return (
                <li key={`${r.brand}-${r.trade_name}`}>
                  <button
                    onClick={() => setTradePick(r)}
                    data-testid={`pro-assign-trade-result-${r.brand}-${r.trade_name.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                      on ? "bg-brand-teal/10" : "hover:bg-brand-linen/30"
                    }`}
                  >
                    <div className="w-8 h-8 border border-brand-linen shrink-0" style={{ background: r.library_hex }} title={`${r.library_name} · ${r.library_hex}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-brand-ink font-medium truncate">
                        {r.brand} · {r.trade_name}
                      </div>
                      <div className="text-[10px] text-brand-muted flex items-center gap-2">
                        <span>→ {r.library_name}</span>
                        <span className="font-mono">{r.library_hex}</span>
                        {typeof r.delta_e === "number" && (
                          <span title="CIE colour distance — lower is closer">Δe {r.delta_e.toFixed(1)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-brand-muted shrink-0">
                      {Math.round((r.confidence || 0) * 100)}%
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {tradePick && (
        <div className="border border-brand-teal/40 bg-brand-teal/5 p-3" data-testid="pro-assign-trade-selected">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 border border-brand-linen shrink-0" style={{ background: tradePick.library_hex }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-brand-ink">
                <span className="font-medium">{tradePick.brand} {tradePick.trade_name}</span>
              </div>
              <div className="text-[11px] text-brand-muted mt-0.5">
                ColourMatch will render using{" "}
                <span className="font-medium text-brand-ink">{tradePick.library_name}</span>
                <span className="font-mono ml-1">({tradePick.library_hex})</span>
                {typeof tradePick.delta_e === "number" && (
                  <span> · closeness Δe {tradePick.delta_e.toFixed(1)}</span>
                )}
              </div>
              <div className="mt-2">
                <Label className="text-brand-ink text-xs">Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. two coats · low sheen"
                  className="mt-1 text-xs"
                  data-testid="pro-assign-trade-notes"
                />
              </div>
            </div>
            <CheckCircle2 className="w-5 h-5 text-brand-teal shrink-0" />
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 4 · Add-tag dialog
// ---------------------------------------------------------------------------
function AddTagDialog({ schemeId, revision, existingLabels, onClose, onDone }) {
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const duplicate = existingLabels.includes(label.trim().toLowerCase());

  const submit = async (e) => {
    e.preventDefault();
    const clean = label.trim();
    if (!clean) return;
    setBusy(true);
    try {
      await api.post(
        `/pro/cms/schemes/${schemeId}/revisions/${revision.revision_number}/tag`,
        { label: clean, painter_note: note.trim() || null },
      );
      toast.success(`Tagged Rev ${revision.revision_number} as '${clean}'`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not add tag.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-add-tag-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">
              Tag Revision {revision.revision_number}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Attach a version tag to this revision. Tags are removable pointers — the revision history stays untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-3">
            Tags are lightweight labels that let you find important milestones. The revision itself stays untouched — remove a tag anytime without losing the history.
          </div>
          <Label className="text-brand-ink">Label *</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. v1 Draft · Client Feedback · Final"
            maxLength={64}
            autoFocus
            className="mt-1.5"
            data-testid="pro-add-tag-label"
          />
          {duplicate && label.trim() && (
            <div className="mt-2 text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-200 px-2 py-1">
              A tag with this label already exists on this scheme.
            </div>
          )}
          <Label className="text-brand-ink mt-3 block">Painter note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Presented at 3pm site meeting"
            rows={2}
            className="mt-1.5"
            data-testid="pro-add-tag-note"
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !label.trim() || duplicate}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-add-tag-submit"
            >
              <TagIcon className="w-4 h-4 mr-2" />
              {busy ? "Adding…" : "Add tag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 · Compare-schemes dialog
// ---------------------------------------------------------------------------
function CompareSchemesDialog({ projectId, schemes, onClose, onOpenScheme }) {
  const [picked, setPicked] = useState(() => schemes.slice(0, Math.min(schemes.length, 3)).map((s) => s.id));
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 4) return cur;
      return [...cur, id];
    });
  };

  const load = useCallback(async () => {
    if (picked.length < 2) { setMatrix(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/pro/cms/projects/${projectId}/compare`, {
        params: { scheme_ids: picked.join(",") },
      });
      setMatrix(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Could not load comparison.");
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, picked]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="pro-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Compare schemes</DialogTitle>
          <DialogDescription className="sr-only">
            Pick 2 to 4 schemes to see them side by side. Every colour is joined live from the ColourMatch library.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-brand-muted mt-2 mb-3">
          Pick 2–4 schemes to see them side-by-side. Every colour is joined live from the ColourMatch library — no stale hex ever renders.
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="pro-compare-picker">
          {schemes.map((s) => {
            const on = picked.includes(s.id);
            const disabled = !on && picked.length >= 4;
            return (
              <button
                key={s.id}
                onClick={() => toggle(s.id)}
                disabled={disabled}
                data-testid={`pro-compare-pick-${s.id}`}
                className={`px-3 h-8 text-xs border transition-colors ${
                  on
                    ? "border-brand-teal bg-brand-teal/10 text-brand-teal"
                    : disabled
                    ? "border-brand-linen bg-brand-linen/20 text-brand-muted cursor-not-allowed"
                    : "border-brand-linen bg-white text-brand-ink hover:border-brand-ink"
                }`}
              >
                {on && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}
                {s.name}
                <span className="ml-1.5 text-[10px] text-brand-muted">{s.assigned_surface_count ?? 0}/16</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-auto border border-brand-linen bg-white" data-testid="pro-compare-matrix">
          {picked.length < 2 ? (
            <div className="p-8 text-sm text-brand-muted text-center">
              Pick at least 2 schemes to compare.
            </div>
          ) : loading ? (
            <div className="p-8 text-sm text-brand-muted flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading comparison…
            </div>
          ) : error ? (
            <div className="p-8 text-sm text-yellow-800 bg-yellow-50 text-center">{error}</div>
          ) : matrix ? (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-brand-linen/40 z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-brand-muted uppercase tracking-wide text-[10px]">Surface</th>
                  {matrix.schemes.map((s) => (
                    <th key={s.id} className="text-left px-3 py-2">
                      <button
                        onClick={() => onOpenScheme(s.id)}
                        data-testid={`pro-compare-header-${s.id}`}
                        className="font-medium text-brand-ink hover:text-brand-teal text-left"
                        title="Open this scheme in the editor"
                      >
                        {s.name}
                      </button>
                      <div className="text-[10px] text-brand-muted font-normal">{s.assigned_surface_count}/16 surfaces</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {["interior", "exterior"].flatMap((cat) => {
                  const catRows = matrix.rows.filter((r) => r.category === cat);
                  return [
                    <tr key={`cat-${cat}`}>
                      <td colSpan={matrix.schemes.length + 1} className="bg-brand-linen/20 px-3 py-1.5 text-[10px] uppercase tracking-wide text-brand-muted font-medium">
                        {cat}
                      </td>
                    </tr>,
                    ...catRows.map((row) => (
                      <tr key={row.surface_slug} className="border-t border-brand-linen" data-testid={`pro-compare-row-${row.surface_slug}`}>
                        <td className="px-3 py-2 text-brand-ink font-medium w-40">
                          {row.surface_display_name}
                        </td>
                        {row.cells.map((cell, i) => (
                          <td key={`${row.surface_slug}-${matrix.schemes[i].id}`} className="px-3 py-2 align-top">
                            {cell.assigned && !cell.colour_stale ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <div
                                  className="w-6 h-6 border border-brand-linen shrink-0"
                                  style={{ background: cell.colour_hex }}
                                  title={`${cell.colour_name} · ${cell.colour_hex}`}
                                />
                                <div className="min-w-0">
                                  <div className="text-brand-ink truncate">{cell.colour_name}</div>
                                  <div className="text-[10px] text-brand-muted font-mono">{cell.colour_hex}</div>
                                </div>
                              </div>
                            ) : cell.colour_stale ? (
                              <div className="flex items-center gap-2 text-yellow-800">
                                <div className="w-6 h-6 border-2 border-dashed border-yellow-400 bg-yellow-50 flex items-center justify-center">
                                  <AlertTriangle className="w-3 h-3" />
                                </div>
                                <span className="text-[11px] italic">Stale colour</span>
                              </div>
                            ) : (
                              <span className="text-brand-muted italic">Not set</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
          ) : null}
        </div>
        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 · Save current scheme as a preset
// ---------------------------------------------------------------------------
function SaveAsPresetDialog({ scheme, onClose }) {
  const [name, setName] = useState(`${scheme?.name || "Scheme"} preset`);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pro/cms/presets", {
        name: name.trim(),
        description: description.trim() || null,
        source_scheme_id: scheme.id,
      });
      const dropped = data?.dropped_surfaces?.length || 0;
      toast.success(
        `Preset '${data.name}' saved (${data.surface_count} surface(s))`
        + (dropped ? ` — ${dropped} skipped` : "")
      );
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save preset.");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pro-save-preset-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Save as preset</DialogTitle>
            <DialogDescription className="sr-only">
              Save this scheme&apos;s assigned surfaces as a reusable painter-scoped preset.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-3">
            Save this scheme&apos;s {scheme?.assigned_surface_count || 0} assigned surface(s) as a reusable preset. You can apply it to any future scheme in one click.
          </div>
          <Label className="text-brand-ink">Preset name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={140}
            autoFocus
            className="mt-1.5"
            data-testid="pro-save-preset-name"
          />
          <Label className="text-brand-ink mt-3 block">Description (optional)</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Warm coastal neutrals for weatherboard homes"
            rows={2}
            className="mt-1.5"
            data-testid="pro-save-preset-desc"
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !name.trim()}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-save-preset-submit"
            >
              <BookmarkPlus className="w-4 h-4 mr-2" />
              {busy ? "Saving…" : "Save preset"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 · Apply preset to current scheme
// ---------------------------------------------------------------------------
function ApplyPresetDialog({ schemeId, onClose, onApplied }) {
  const [presets, setPresets] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("overwrite");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pro/cms/presets");
        setPresets(data.items || []);
      } catch {
        setPresets([]);
      }
    })();
  }, []);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/cms/schemes/${schemeId}/apply-preset`, {
        preset_id: selected.id,
        mode,
        painter_note: note.trim() || null,
      });
      const applied = data.surfaces_applied;
      const skipped = data.surfaces_skipped?.length || 0;
      toast.success(
        `Applied '${data.preset_name}' — ${applied} surface(s)`
        + (skipped ? `, ${skipped} skipped` : "")
      );
      onApplied();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not apply preset.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="pro-apply-preset-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Apply a preset</DialogTitle>
          <DialogDescription className="sr-only">
            Apply a saved preset to this scheme. The mutation is added to the append-only revision history so nothing is ever lost.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-brand-muted mt-2 mb-3">
          Applying a preset adds it to the append-only revision history. Nothing is ever lost — you can restore any previous state from the timeline.
        </div>
        <div className="max-h-[280px] overflow-y-auto border border-brand-linen bg-white" data-testid="pro-apply-preset-list">
          {presets === null ? (
            <div className="p-6 text-sm text-brand-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading presets…
            </div>
          ) : presets.length === 0 ? (
            <div className="p-6 text-sm text-brand-muted text-center">
              No presets yet — save one from any scheme to reuse it here.
            </div>
          ) : (
            <ul className="divide-y divide-brand-linen">
              {presets.map((p) => {
                const on = selected?.id === p.id;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => setSelected(p)}
                      data-testid={`pro-apply-preset-pick-${p.id}`}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                        on ? "bg-brand-teal/10" : "hover:bg-brand-linen/30"
                      }`}
                    >
                      <Bookmark className={`w-4 h-4 shrink-0 ${on ? "text-brand-teal" : "text-brand-muted"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-brand-ink font-medium truncate">{p.name}</div>
                        {p.description && <div className="text-[11px] text-brand-muted truncate">{p.description}</div>}
                      </div>
                      <div className="text-[10px] text-brand-muted shrink-0">{p.surface_count} surface(s)</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {selected && (
          <div className="mt-3 space-y-3" data-testid="pro-apply-preset-config">
            <div>
              <Label className="text-brand-ink">Mode</Label>
              <div className="mt-1.5 flex gap-2">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="radio"
                    name="apply-mode"
                    value="overwrite"
                    checked={mode === "overwrite"}
                    onChange={(e) => setMode(e.target.value)}
                    data-testid="pro-apply-mode-overwrite"
                    className="peer sr-only"
                  />
                  <div className="p-2.5 border border-brand-linen peer-checked:border-brand-teal peer-checked:bg-brand-teal/5">
                    <div className="text-sm text-brand-ink font-medium">Overwrite</div>
                    <div className="text-[11px] text-brand-muted mt-0.5">Replace colours on every matching surface</div>
                  </div>
                </label>
                <label className="flex-1 cursor-pointer">
                  <input
                    type="radio"
                    name="apply-mode"
                    value="fill_empty"
                    checked={mode === "fill_empty"}
                    onChange={(e) => setMode(e.target.value)}
                    data-testid="pro-apply-mode-fill-empty"
                    className="peer sr-only"
                  />
                  <div className="p-2.5 border border-brand-linen peer-checked:border-brand-teal peer-checked:bg-brand-teal/5">
                    <div className="text-sm text-brand-ink font-medium">Fill empty only</div>
                    <div className="text-[11px] text-brand-muted mt-0.5">Preserve any surfaces you&apos;ve already assigned</div>
                  </div>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-brand-ink">Painter note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Applied Coastal Calm as starting point"
                rows={2}
                className="mt-1.5"
                data-testid="pro-apply-preset-note"
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!selected || busy}
            className="bg-brand-navy text-white hover:bg-brand-teal/90"
            data-testid="pro-apply-preset-submit"
          >
            <Layers className="w-4 h-4 mr-2" />
            {busy ? "Applying…" : selected ? `Apply ${selected.name}` : "Pick a preset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 · Presets manager (list + delete)
// ---------------------------------------------------------------------------
function PresetsDialog({ onClose }) {
  const [presets, setPresets] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pro/cms/presets");
      setPresets(data.items || []);
    } catch {
      setPresets([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deletePreset = async (id) => {
    if (!window.confirm("Delete this preset? It won't affect any schemes that have already used it.")) return;
    try {
      await api.delete(`/pro/cms/presets/${id}`);
      toast.success("Preset deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not delete preset.");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="pro-presets-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Colour presets</DialogTitle>
          <DialogDescription className="sr-only">
            Manage your reusable colour presets — the templates saved from schemes across your projects.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-brand-muted mt-2 mb-3">
          Presets are your reusable colour templates. Save one from any scheme via the &quot;Save as preset&quot; button, then apply it to future schemes with one click.
        </div>
        <div className="max-h-[440px] overflow-y-auto border border-brand-linen bg-white" data-testid="pro-presets-list">
          {presets === null ? (
            <div className="p-6 text-sm text-brand-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : presets.length === 0 ? (
            <div className="p-8 text-sm text-brand-muted text-center">
              No presets yet. Open a scheme with assigned colours and click &ldquo;Save as preset&rdquo; to create your first one.
            </div>
          ) : (
            <ul className="divide-y divide-brand-linen">
              {presets.map((p) => (
                <li
                  key={p.id}
                  className="flex items-start gap-3 p-3 hover:bg-brand-linen/20 transition-colors"
                  data-testid={`pro-preset-row-${p.id}`}
                >
                  <Bookmark className="w-4 h-4 shrink-0 text-brand-teal mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-ink">{p.name}</div>
                    {p.description && <div className="text-[11px] text-brand-muted mt-0.5">{p.description}</div>}
                    <div className="text-[10px] text-brand-muted mt-1">
                      {p.surface_count} surface(s) · created {new Date(p.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => deletePreset(p.id)}
                    data-testid={`pro-preset-delete-${p.id}`}
                    className="text-brand-muted hover:text-red-600 transition-colors shrink-0 mt-0.5"
                    title="Delete preset"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 5 · Share-with-homeowner dialog
// ---------------------------------------------------------------------------
function ShareProjectDialog({ projectId, projectName, onClose }) {
  const [state, setState] = useState({ loading: true, link: null });
  const [homeownerName, setHomeownerName] = useState("");
  const [homeownerEmail, setHomeownerEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pro/cms/projects/${projectId}/share`);
      setState({ loading: false, link: data.active ? data.link : null });
      if (data.link) {
        setHomeownerName(data.link.homeowner_name || "");
        setHomeownerEmail(data.link.homeowner_email || "");
        setNote(data.link.note || "");
      }
    } catch (e) {
      setState({ loading: false, link: null });
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load share link.");
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/cms/projects/${projectId}/share`, {
        homeowner_name: homeownerName.trim() || null,
        homeowner_email: homeownerEmail.trim() || null,
        note: note.trim() || null,
      });
      setState({ loading: false, link: data });
      toast.success(state.link ? "Share link updated" : "Share link ready");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create share link.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this share link? The homeowner will no longer be able to view or approve any presented schemes via the current URL.")) return;
    setBusy(true);
    try {
      await api.delete(`/pro/cms/projects/${projectId}/share`);
      setState({ loading: false, link: null });
      toast.success("Share link revoked");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not revoke share link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!state.link?.token) return;
    const url = `${window.location.origin}/shared/${state.link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — please select and copy the link manually.");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="pro-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">
            Share {projectName}
          </DialogTitle>
          <DialogDescription className="text-xs text-brand-muted mt-1">
            The homeowner will see only schemes you&apos;ve marked as Presented — never drafts, deleted, or archived work.
          </DialogDescription>
        </DialogHeader>

        {state.loading ? (
          <div className="p-8 text-sm text-brand-muted flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {state.link ? (
              <>
                <Label className="text-brand-ink text-[11px] uppercase tracking-wide">Homeowner link</Label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/shared/${state.link.token}`}
                    data-testid="pro-share-link-url"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 border border-brand-linen bg-brand-linen/20 px-3 h-9 text-sm text-brand-ink font-mono truncate"
                  />
                  <Button
                    type="button"
                    onClick={copy}
                    data-testid="pro-share-copy-btn"
                    variant="outline"
                    className="shrink-0"
                  >
                    <Link2 className="w-4 h-4 mr-1.5" /> Copy
                  </Button>
                </div>
                <div className="text-[10px] text-brand-muted">
                  Views: {state.link.view_count || 0}
                  {state.link.last_viewed_at && (
                    <> · Last viewed {new Date(state.link.last_viewed_at).toLocaleString()}</>
                  )}
                </div>
                <div className="border-t border-brand-linen pt-3">
                  <button
                    onClick={() => setCommentsOpen(true)}
                    data-testid="pro-share-open-inbox-btn"
                    className="text-xs text-brand-teal hover:underline inline-flex items-center gap-1"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> View homeowner comments
                  </button>
                </div>
              </>
            ) : (
              <div className="text-sm text-brand-ink/80">
                No share link yet. Create one below to invite the homeowner to review your presented schemes.
              </div>
            )}

            <div className="border-t border-brand-linen pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-brand-ink">Homeowner name (optional)</Label>
                <Input
                  value={homeownerName}
                  onChange={(e) => setHomeownerName(e.target.value)}
                  maxLength={140}
                  className="mt-1.5"
                  data-testid="pro-share-homeowner-name"
                />
              </div>
              <div>
                <Label className="text-brand-ink">Homeowner email (optional)</Label>
                <Input
                  value={homeownerEmail}
                  onChange={(e) => setHomeownerEmail(e.target.value)}
                  maxLength={254}
                  className="mt-1.5"
                  data-testid="pro-share-homeowner-email"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-brand-ink">Painter note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  className="mt-1.5"
                  placeholder="e.g. Please review before Friday"
                  data-testid="pro-share-note"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex-wrap gap-2">
          {state.link && (
            <Button
              type="button"
              variant="outline"
              onClick={revoke}
              disabled={busy}
              data-testid="pro-share-revoke-btn"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              Revoke link
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          <Button
            type="button"
            onClick={create}
            disabled={busy}
            data-testid="pro-share-save-btn"
            className="bg-brand-navy text-white hover:bg-brand-teal/90"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {busy ? "Saving…" : state.link ? "Update details" : "Create link"}
          </Button>
        </DialogFooter>

        {commentsOpen && state.link && (
          <ProjectCommentsInboxDialog
            projectId={projectId}
            onClose={() => setCommentsOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 5 · Homeowner comments inbox (aggregates across every scheme)
// ---------------------------------------------------------------------------
function ProjectCommentsInboxDialog({ projectId, onClose }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    // Painter API doesn't have a project-wide list yet — walk the schemes.
    try {
      const { data: sd } = await api.get(`/pro/cms/projects/${projectId}/schemes`);
      const buckets = await Promise.all(
        (sd.items || []).map(async (s) => {
          const { data } = await api.get(`/pro/cms/schemes/${s.id}/comments`);
          return (data.items || []).map((c) => ({ ...c, scheme_name: s.name }));
        })
      );
      const flat = buckets.flat().sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setRows(flat);
    } catch {
      setRows([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (schemeId, commentId) => {
    try {
      await api.post(`/pro/cms/schemes/${schemeId}/comments/${commentId}/resolve`);
      toast.success("Comment resolved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not resolve.");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="pro-share-comments-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Homeowner comments</DialogTitle>
          <DialogDescription className="sr-only">
            View every comment the homeowner has left across this project&apos;s schemes.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[440px] overflow-y-auto border border-brand-linen bg-white mt-3">
          {rows === null ? (
            <div className="p-6 text-sm text-brand-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-sm text-brand-muted text-center">
              No comments yet. When the homeowner leaves feedback it will show up here.
            </div>
          ) : (
            <ul className="divide-y divide-brand-linen">
              {rows.map((c) => (
                <li key={c.id} className="p-3" data-testid={`pro-comment-row-${c.id}`}>
                  <div className="text-sm text-brand-ink">{c.body}</div>
                  <div className="text-[10px] text-brand-muted mt-1 flex flex-wrap items-center gap-2">
                    <span>{c.commenter_name || "Anonymous"}</span>
                    {c.commenter_email && <span>· {c.commenter_email}</span>}
                    {c.scheme_name && <span>· on {c.scheme_name}</span>}
                    {c.surface_slug && <span>· {c.surface_slug}</span>}
                    <span>· {new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1.5">
                    {c.resolved ? (
                      <span className="text-[10px] uppercase tracking-wide text-brand-teal bg-brand-teal/10 px-1.5 h-4 inline-flex items-center">
                        Resolved
                      </span>
                    ) : (
                      <button
                        onClick={() => resolve(c.scheme_id, c.id)}
                        data-testid={`pro-comment-resolve-${c.id}`}
                        className="text-[11px] text-brand-teal hover:underline"
                      >
                        Mark resolved
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Phase 7 — inline client-details editor. Reuses PATCH /pro/cms/projects/{id}.
// ---------------------------------------------------------------------------
function EditClientDialog({ open, project, onClose, onSaved }) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !project) return;
    setClientName(project.client_name || "");
    setClientEmail(project.client_email || "");
    setSiteAddress(project.site_address || "");
    setNotes(project.notes || "");
  }, [open, project]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!project) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/pro/cms/projects/${project.id}`, {
        client_name: clientName.trim() || null,
        client_email: clientEmail.trim() || null,
        site_address: siteAddress.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success("Client details saved");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="pro-project-edit-client-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Edit client details</DialogTitle>
            <DialogDescription>
              Populates every future document. Client email also enables the automatic
              &ldquo;colour schedule presented&rdquo; email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Label htmlFor="edit-client-name">Client name</Label>
              <Input
                id="edit-client-name"
                data-testid="pro-project-edit-client-name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                maxLength={140}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-client-email">Client email</Label>
              <Input
                id="edit-client-email"
                type="email"
                data-testid="pro-project-edit-client-email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                maxLength={254}
                placeholder="hello@homeowner.example"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-client-site">Site address</Label>
              <Input
                id="edit-client-site"
                data-testid="pro-project-edit-client-site"
                value={siteAddress}
                onChange={(e) => setSiteAddress(e.target.value)}
                maxLength={280}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-client-notes">Notes</Label>
              <Textarea
                id="edit-client-notes"
                data-testid="pro-project-edit-client-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="pro-project-edit-client-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
